/* eslint-disable react/no-inline-styles, no-inline-styles */
import React, { useState, useEffect } from 'react';
import { Student } from '../types';
import { API_URL, apiFetch } from '../services/api';

interface PackageSelectionScreenProps {
    student: Student;
    onLogout: () => void;
}

interface PricingPackage {
    id: string;
    name: string;
    price: string;
    period: string;
    description: string;
    features: string[];
    isPopular: boolean;
    ctaText: string;
    color: string;
    durationDays: number;
    isCustomLesson?: boolean;
}

interface PricingData {
    title: string;
    subtitle: string;
    billingCycles: {
        monthly: { label: string; multiplier: number; periodLabel: string; badge: string; };
        sixMonths: { label: string; multiplier: number; periodLabel: string; badge: string; };
        untilExam: { label: string; multiplier: number; periodLabel: string; badge: string; };
    };
    packages: PricingPackage[];
}

export const PackageSelectionScreen: React.FC<PackageSelectionScreenProps> = ({ student, onLogout }) => {
    const [pricing, setPricing] = useState<PricingData | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'sixMonths' | 'untilExam'>('monthly');

    // Stepper State
    const [step, setStep] = useState(1);
    const [selectedPkg, setSelectedPkg] = useState<PricingPackage | null>(null);
    const [customLessons, setCustomLessons] = useState<{count: number; subjectStr: string}>({ count: 1, subjectStr: '' });
    const [contactInfo, setContactInfo] = useState({
        phone: student.phone || '',
        parentPhone: '',
        parentName: ''
    });
    const [billingInfo, setBillingInfo] = useState({
        address: '',
        city: '',
        taxNumber: '',
        taxOffice: ''
    });

    // Payment States
    const [paymentMethod, setPaymentMethod] = useState<'credit_card' | 'bank_transfer'>('credit_card');
    const [cardholderName, setCardholderName] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [cardExpiry, setCardExpiry] = useState('');
    const [cardCvc, setCardCvc] = useState('');

    useEffect(() => {
        fetchPricing();
    }, []);

    const fetchPricing = async () => {
        try {
            const response = await apiFetch(`${API_URL}/api/landing-content`);
            const data = await response.json();
            if (data?.pricing?.packages?.length > 0) {
                setPricing(data.pricing);
            } else {
                setPricing({
                    title: 'Size Uygun Planı Seçin',
                    subtitle: 'İhtiyaçlarınıza göre özelleştirilmiş eğitim çözümlerimiz',
                    packages: []
                });
            }
        } catch (err) {
            console.error('Error fetching pricing:', err);
            setError('Paket bilgileri yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleNextStep = () => {
        if (step === 1 && !selectedPkg) {
            setError('Lütfen devam etmek için bir paket seçin');
            return;
        }
        if (step === 2 && !contactInfo.phone) {
            setError('Lütfen iletişim için bir telefon numarası girin');
            return;
        }
        setError('');
        setStep(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePrevStep = () => {
        setError('');
        setStep(prev => prev - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePurchaseRequest = async () => {
        if (!selectedPkg) return;

        if (selectedPkg.isCustomLesson && (!customLessons.count || !customLessons.subjectStr.trim())) {
            setError('Lütfen ders sayısını ve ders isimlerini girin.');
            return;
        }

        if (paymentMethod === 'credit_card') {
            if (!cardholderName.trim() || !cardNumber.trim() || !cardExpiry.trim() || !cardCvc.trim()) {
                setError('Lütfen kart bilgilerini eksiksiz girin.');
                return;
            }
            if (cardNumber.replace(/\s/g, '').length < 16) {
                setError('Geçersiz kart numarası.');
                return;
            }
        }

        setSubmitting(selectedPkg.id);
        setError('');

        // Calculate dynamic duration and price
        let finalDuration = selectedPkg.durationDays || 30;
        let finalPrice = selectedPkg.price;

        const basePrice = parseInt(selectedPkg.price.replace(/[^\d]/g, '')) || 0;
        let finalPriceNum = basePrice;
        if (selectedPkg.isCustomLesson) {
            finalPriceNum = basePrice * customLessons.count;
        }

        const cycleConfig = pricing?.billingCycles?.[billingCycle];
        if (cycleConfig) {
            if (billingCycle === 'sixMonths') finalDuration = 180;
            else if (billingCycle === 'untilExam') finalDuration = 365;

            finalPrice = `${(finalPriceNum * cycleConfig.multiplier).toLocaleString('tr-TR')} ₺`;
        } else {
            finalPrice = `${finalPriceNum.toLocaleString('tr-TR')} ₺`;
        }

        let finalPackageName = `${selectedPkg.name} (${cycleConfig ? cycleConfig.label : 'Aylık'})`;
        if (selectedPkg.isCustomLesson) {
            finalPackageName += ` - ${customLessons.count} Ders (${customLessons.subjectStr})`;
        }

        try {
            const response = await apiFetch(`${API_URL}/api/landing-content/purchase-request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    studentId: student.id.toString(),
                    studentName: student.name,
                    studentEmail: student.email || '',
                    packageId: selectedPkg.id,
                    packageName: finalPackageName,
                    packagePrice: finalPrice,
                    packageDurationDays: finalDuration,
                    paymentMethod: paymentMethod,
                    paymentDetails: paymentMethod === 'credit_card' ? {
                        cardholderName,
                        cardNumber: '**** **** **** ' + cardNumber.replace(/\s/g, '').slice(-4),
                    } : {
                        senderName: student.name
                    },
                    contactInfo,
                    billingInfo
                })
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Talep oluşturulamadı');
            }
            setSubmitted(true);
        } catch (err: any) {
            setError(err.message || 'Bir hata oluştu');
        } finally {
            setSubmitting(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-slate-500 text-sm font-medium">Yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (submitted || student.packageStatus === 'pending') {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="max-w-lg w-full text-center">
                    <div className="bg-white border border-slate-100 rounded-[2.5rem] p-12 shadow-2xl shadow-slate-200/50">
                        <div className="w-24 h-24 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="text-5xl">⏳</span>
                        </div>
                        <h2 className="text-2xl font-black text-slate-900 mb-3 tracking-tight">Talebiniz Alındı!</h2>
                        <p className="text-slate-500 font-medium leading-relaxed mb-8">
                            Paket satın alma talebiniz yöneticimize iletildi. Onay sürecinden sonra sisteme tam erişim sağlayabileceksiniz.
                        </p>
                        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mb-8">
                            <p className="text-blue-700 text-sm font-bold">
                                💡 Onay süreci genellikle 24 saat içinde tamamlanır.
                            </p>
                        </div>
                        <button
                            onClick={onLogout}
                            className="px-8 py-4 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-lg"
                        >
                            Çıkış Yap
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const steps = [
        { id: 1, label: 'Paket Seçimi' },
        { id: 2, label: 'İletişim Bilgileri' },
        { id: 3, label: 'Fatura Bilgileri' },
        { id: 4, label: 'Ödeme Bilgileri' }
    ];

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200/60 bg-white/80 backdrop-blur-md sticky top-0 z-50">
                <div className="flex items-center gap-3">
                    <img src="/supervisor-logo-2026.png" alt="Logo" className="w-10 h-10 rounded-xl" />
                    <span className="text-slate-900 font-black text-lg tracking-tight">Supervisor Eğitim</span>
                </div>
                <div className="flex items-center gap-4">
                    <span className="hidden md:block text-slate-400 text-[10px] font-black uppercase tracking-widest">Hoş Geldin, <span className="text-slate-900">{student.name}</span></span>
                    <button
                        onClick={onLogout}
                        className="px-4 py-2 bg-slate-50 hover:bg-slate-100 text-slate-900 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border border-slate-200"
                    >
                        Çıkış Yap
                    </button>
                </div>
            </div>

            {/* Stepper Navigation */}
            <div className="max-w-4xl mx-auto px-6 pt-12">
                <div className="flex items-center justify-between relative mb-12">
                    {/* Connecting Line */}
                    <div className="absolute top-1/2 left-0 w-full h-0.5 bg-slate-200 -translate-y-1/2 z-0"></div>
                    <div
                        className="absolute top-1/2 left-0 h-0.5 bg-blue-600 -translate-y-1/2 z-0 transition-all duration-500"
                        style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%` }}
                    ></div>

                    {steps.map((s) => (
                        <div key={s.id} className="relative z-10 flex flex-col items-center gap-3">
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 border-2 ${step >= s.id
                                    ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/20'
                                    : 'bg-white border-slate-200 text-slate-300'
                                    }`}
                            >
                                {step > s.id ? '✓' : s.id}
                            </div>
                            <span className={`text-[10px] md:text-xs font-black uppercase tracking-tighter transition-all duration-500 ${step >= s.id ? 'text-slate-900' : 'text-slate-300'
                                }`}>
                                {s.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Content Area */}
            <div className="max-w-6xl mx-auto px-6 pb-24">
                {error && (
                    <div className="max-w-md mx-auto mb-8 bg-red-50 border border-red-100 rounded-2xl p-4 text-center animate-shake">
                        <p className="text-red-600 text-xs font-bold font-black uppercase tracking-widest">{error}</p>
                    </div>
                )}

                {/* STEP 1: PACKAGE SELECTION */}
                {step === 1 && (
                    <div className="animate-fade-in-up">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4 tracking-tight">Eğitime Başlamak İçin Paket Seçin</h2>
                            <p className="text-slate-500 max-w-xl mx-auto font-medium">İhtiyaçlarınıza en uygun paketi seçerek kişiselleştirilmiş eğitim yolculuğunuza hemen başlayabilirsiniz.</p>

                            {/* Billing Cycle Selector */}
                            <div className="flex justify-center mt-10">
                                <div className="bg-white p-1.5 rounded-2xl border border-slate-200 shadow-sm flex gap-1">
                                    {(['monthly', 'sixMonths', 'untilExam'] as const).map((cycleKey) => {
                                        const tabConfig = pricing?.billingCycles?.[cycleKey] || {
                                            label: cycleKey === 'monthly' ? 'Aylık' : cycleKey === 'sixMonths' ? '6 Aylık' : 'Sınava Kadar',
                                            badge: cycleKey === 'sixMonths' ? '%15 İndirim' : cycleKey === 'untilExam' ? 'Efsane' : ''
                                        };
                                        return (
                                            <button
                                                key={cycleKey}
                                                onClick={() => setBillingCycle(cycleKey)}
                                                className={`px-6 py-3 rounded-xl font-black text-[10px] tracking-widest uppercase transition-all relative ${billingCycle === cycleKey
                                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                                                    : 'text-slate-400 hover:text-slate-900 hover:bg-slate-50'
                                                    }`}
                                            >
                                                {tabConfig.label}
                                                {tabConfig.badge && (
                                                    <span className={`absolute -top-3 -right-2 px-2 py-0.5 rounded-full text-[8px] font-black uppercase ${billingCycle === cycleKey ? 'bg-orange-500 text-white' : 'bg-blue-100 text-blue-600 border border-blue-200'
                                                        }`}>
                                                        {tabConfig.badge}
                                                    </span>
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {pricing?.packages.filter(p => (p as any).isActive !== false).map((pkg) => {
                                let displayPrice = pkg.price;
                                let displayPeriod = pkg.period;
                                const basePrice = parseInt(pkg.price.replace(/[^\d]/g, '')) || 0;
                                const cycleConfig = pricing?.billingCycles?.[billingCycle];
                                const isSelected = selectedPkg?.id === pkg.id;

                                let calculatedPrice = basePrice;

                                if (cycleConfig) {
                                    displayPrice = (calculatedPrice * cycleConfig.multiplier).toLocaleString('tr-TR');
                                    displayPeriod = cycleConfig.periodLabel || displayPeriod;
                                } else {
                                    displayPrice = calculatedPrice.toLocaleString('tr-TR');
                                }

                                return (
                                    <div
                                        key={pkg.id}
                                        onClick={() => { setSelectedPkg(pkg); setError(''); }}
                                        className={`relative bg-white border rounded-[2rem] p-8 transition-all duration-500 cursor-pointer group flex flex-col ${isSelected
                                            ? 'border-blue-500 ring-4 ring-blue-50 shadow-2xl shadow-blue-500/10 -translate-y-2'
                                            : 'border-slate-100 hover:border-slate-200 hover:-translate-y-1 shadow-xl shadow-slate-200/50'
                                            }`}
                                    >
                                        {pkg.isPopular && (
                                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-[10px] font-black uppercase px-4 py-1.5 rounded-full shadow-lg">
                                                ⭐ En Çok Tercih Edilen
                                            </div>
                                        )}

                                        <div className="mb-6">
                                            <h3 className="text-xl font-black text-slate-900 mb-2">{pkg.name}</h3>
                                            <p className="text-slate-500 text-xs leading-relaxed font-bold">{pkg.description}</p>
                                        </div>

                                        <div className="mb-8">
                                            <span className="text-4xl font-black text-slate-900 tracking-tighter">{displayPrice}₺</span>
                                            <span className="text-slate-400 text-sm font-black uppercase tracking-widest ml-1">{displayPeriod}</span>
                                        </div>

                                        <ul className="space-y-4 mb-10 flex-1">
                                            {pkg.features.map((feature, i) => (
                                                <li key={i} className="flex items-start gap-3 text-xs text-slate-600">
                                                    <span className="text-blue-600 mt-0.5 flex-shrink-0 font-black">✓</span>
                                                    <span className="font-bold">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        <div className={`mt-auto w-full py-5 rounded-2xl font-black text-[10px] uppercase tracking-widest text-center transition-all ${isSelected
                                            ? 'bg-blue-600 text-white shadow-xl shadow-blue-500/20'
                                            : 'bg-slate-50 text-slate-400 group-hover:bg-slate-100 group-hover:text-slate-900'
                                            }`}>
                                            {isSelected ? 'Paket Seçildi' : 'Bu Paketi Seç'}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-12 flex justify-center">
                            <button
                                onClick={handleNextStep}
                                disabled={!selectedPkg}
                                className={`px-12 py-5 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${selectedPkg
                                    ? 'bg-slate-900 text-white hover:bg-black shadow-xl shadow-slate-900/10'
                                    : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                                    }`}
                            >
                                Sonraki Adım →
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 2: CONTACT INFORMATION */}
                {step === 2 && (
                    <div className="max-w-xl mx-auto animate-fade-in-up">
                        <div className="bg-white border border-slate-200 rounded-[2.5rem] p-10 shadow-2xl shadow-slate-200/50">
                            <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">İletişim Bilgileri</h2>
                            <p className="text-slate-500 text-sm font-medium mb-10">Danışmanlarımızın size ulaşabilmesi için bilgilerinizi güncelleyin.</p>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2 px-1">Telefon Numaranız</label>
                                    <input
                                        type="tel"
                                        placeholder="05xx xxx xx xx"
                                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/5 transition-all font-bold"
                                        value={contactInfo.phone}
                                        onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2 px-1">Veli Ad Soyad (İsteğe Bağlı)</label>
                                    <input
                                        type="text"
                                        placeholder="Ad Soyad"
                                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/5 transition-all font-bold"
                                        value={contactInfo.parentName}
                                        onChange={(e) => setContactInfo({ ...contactInfo, parentName: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2 px-1">Veli Telefon (İsteğe Bağlı)</label>
                                    <input
                                        type="tel"
                                        placeholder="05xx xxx xx xx"
                                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/5 transition-all font-bold"
                                        value={contactInfo.parentPhone}
                                        onChange={(e) => setContactInfo({ ...contactInfo, parentPhone: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-4 mt-12">
                                <button
                                    onClick={handlePrevStep}
                                    className="flex-1 py-5 bg-slate-50 hover:bg-slate-100 text-slate-900 font-black text-[10px] uppercase tracking-widest rounded-2xl transition-all border border-slate-200"
                                >
                                    ← Geri
                                </button>
                                <button
                                    onClick={handleNextStep}
                                    className="flex-[2] py-5 bg-slate-900 hover:bg-black text-white font-black text-[10px] uppercase tracking-widest rounded-2xl transition-all shadow-xl shadow-slate-900/10"
                                >
                                    Sonraki Adım →
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 3: BILLING INFORMATION */}
                {step === 3 && (
                    <div className="max-w-xl mx-auto animate-fade-in-up">
                        <div className="bg-white border border-slate-200 rounded-[2.5rem] p-10 shadow-2xl shadow-slate-200/50">
                            <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">Fatura Bilgileri</h2>
                            <p className="text-slate-500 text-sm font-medium mb-10">Ödeme sonrası adınıza veya kurumunuza düzenlenecek fatura için gerekli bilgiler.</p>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2 px-1">Fatura Adresi</label>
                                    <textarea
                                        placeholder="Açık Adres"
                                        rows={3}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/5 transition-all font-bold resize-none"
                                        value={billingInfo.address}
                                        onChange={(e) => setBillingInfo({ ...billingInfo, address: e.target.value })}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2 px-1">Şehir</label>
                                        <input
                                            type="text"
                                            placeholder="Şehir"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/5 transition-all font-bold"
                                            value={billingInfo.city}
                                            onChange={(e) => setBillingInfo({ ...billingInfo, city: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2 px-1">Vergi Dairesi (Opsiyonel)</label>
                                        <input
                                            type="text"
                                            placeholder="Vergi Dairesi"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/5 transition-all font-bold"
                                            value={billingInfo.taxOffice}
                                            onChange={(e) => setBillingInfo({ ...billingInfo, taxOffice: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2 px-1">TC Kimlik / Vergi No</label>
                                    <input
                                        type="text"
                                        placeholder="TCKN veya Vergi No"
                                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 text-slate-900 placeholder:text-slate-300 focus:outline-none focus:border-blue-500/50 focus:ring-4 focus:ring-blue-500/5 transition-all font-bold"
                                        value={billingInfo.taxNumber}
                                        onChange={(e) => setBillingInfo({ ...billingInfo, taxNumber: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-4 mt-12">
                                <button
                                    onClick={handlePrevStep}
                                    className="flex-1 py-5 bg-slate-50 hover:bg-slate-100 text-slate-900 font-black text-[10px] uppercase tracking-widest rounded-2xl transition-all border border-slate-200"
                                >
                                    ← Geri
                                </button>
                                <button
                                    onClick={handleNextStep}
                                    className="flex-[2] py-5 bg-slate-900 hover:bg-black text-white font-black text-[10px] uppercase tracking-widest rounded-2xl transition-all shadow-xl shadow-slate-900/10"
                                >
                                    Sonraki Adım →
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 4: SUMMARY & PAYMENT REQUEST */}
                {step === 4 && selectedPkg && (
                    <div className="max-w-xl mx-auto animate-fade-in-up">
                        <div className="bg-white border border-slate-200 rounded-[2.5rem] p-10 shadow-2xl shadow-slate-200/50">
                            <h2 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">Son Onay</h2>
                            <p className="text-slate-500 text-sm font-medium mb-10">Lütfen seçtiğiniz paketi ve bilgilerinizi kontrol edin.</p>

                            <div className="bg-slate-50 rounded-3xl p-8 mb-8 border border-slate-200/60">
                                <div className="flex items-center justify-between mb-6 pb-6 border-b border-slate-200">
                                    <div>
                                        <h4 className="text-slate-900 font-black text-lg tracking-tight">{selectedPkg.name}</h4>
                                        <p className="text-blue-600 text-[10px] font-black uppercase tracking-widest">
                                            {pricing?.billingCycles?.[billingCycle]?.label || (billingCycle === 'monthly' ? 'Aylık Ödeme' : billingCycle === 'sixMonths' ? '6 Aylık Ödeme' : 'Sınava Kadar Ödeme')}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-2xl font-black text-slate-900">
                                            {(() => {
                                                const basePrice = parseInt(selectedPkg.price.replace(/[^\d]/g, '')) || 0;
                                                let finalPriceNum = basePrice;
                                                if (selectedPkg.isCustomLesson) {
                                                    finalPriceNum = basePrice * customLessons.count;
                                                }
                                                const cycleConfig = pricing?.billingCycles?.[billingCycle];
                                                if (cycleConfig) return (finalPriceNum * cycleConfig.multiplier).toLocaleString('tr-TR');
                                                return finalPriceNum.toLocaleString('tr-TR');
                                            })()}₺
                                        </span>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex justify-between text-[10px]">
                                        <span className="text-slate-400 font-black uppercase tracking-widest">İletişim:</span>
                                        <span className="text-slate-900 font-black">{contactInfo.phone}</span>
                                    </div>
                                    <div className="flex justify-between text-[10px]">
                                        <span className="text-slate-400 font-black uppercase tracking-widest">Fatura (Şehir):</span>
                                        <span className="text-slate-900 font-black">{billingInfo.city || '-'}</span>
                                    </div>
                                </div>
                            </div>

                            {selectedPkg.isCustomLesson && (
                                <div className="bg-purple-50/50 border border-purple-100 rounded-3xl p-6 mb-8 space-y-4">
                                    <h4 className="text-purple-900 font-bold text-sm">Özel Ders Paketi Seçenekleri</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div className="sm:col-span-1">
                                            <label className="block text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">Ders Sayısı</label>
                                            <input 
                                                type="number" min="1" max="10" 
                                                value={customLessons.count} 
                                                onChange={(e) => setCustomLessons(prev => ({...prev, count: parseInt(e.target.value)||1}))}
                                                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-slate-900 font-bold focus:outline-none focus:border-blue-500"
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="block text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">Ders İsimleri (Örn: Matematik, Fizik)</label>
                                            <input 
                                                type="text" 
                                                value={customLessons.subjectStr} 
                                                onChange={(e) => setCustomLessons(prev => ({...prev, subjectStr: e.target.value}))}
                                                placeholder="Satın almak istediğiniz dersleri yazın..."
                                                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-slate-900 text-sm focus:outline-none focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="mb-8">
                                <h3 className="text-xs font-bold text-slate-900 mb-4 uppercase tracking-widest">Ödeme Yöntemi</h3>
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <button 
                                        type="button"
                                        onClick={() => setPaymentMethod('credit_card')}
                                        className={`py-4 rounded-2xl font-bold text-xs uppercase tracking-wider border flex flex-col items-center gap-2 transition-all ${
                                            paymentMethod === 'credit_card'
                                                ? 'border-blue-500 bg-blue-50/20 text-blue-600 shadow-sm shadow-blue-500/10'
                                                : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                                        }`}
                                    >
                                        <span className="text-2xl">💳</span>
                                        <span>Kredi / Banka Kartı</span>
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setPaymentMethod('bank_transfer')}
                                        className={`py-4 rounded-2xl font-bold text-xs uppercase tracking-wider border flex flex-col items-center gap-2 transition-all ${
                                            paymentMethod === 'bank_transfer'
                                                ? 'border-blue-500 bg-blue-50/20 text-blue-600 shadow-sm shadow-blue-500/10'
                                                : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                                        }`}
                                    >
                                        <span className="text-2xl">🏦</span>
                                        <span>IBAN / EFT / Havale</span>
                                    </button>
                                </div>

                                {paymentMethod === 'credit_card' ? (
                                    <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 space-y-4">
                                        <h4 className="text-slate-800 font-bold text-xs uppercase tracking-wider">Kart Bilgileri</h4>
                                        <div>
                                            <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Kart Sahibi Ad Soyad</label>
                                            <input 
                                                type="text" 
                                                value={cardholderName} 
                                                onChange={e => setCardholderName(e.target.value)}
                                                placeholder="Kart Üzerindeki İsim" 
                                                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:border-blue-500" 
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Kart Numarası</label>
                                            <input 
                                                type="text" 
                                                maxLength={19}
                                                value={cardNumber} 
                                                onChange={e => {
                                                    const val = e.target.value.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim();
                                                    setCardNumber(val);
                                                }}
                                                placeholder="0000 0000 0000 0000" 
                                                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:border-blue-500" 
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Son Kullanma Tarihi</label>
                                                <input 
                                                    type="text" 
                                                    maxLength={5}
                                                    value={cardExpiry} 
                                                    onChange={e => {
                                                        let val = e.target.value.replace(/\D/g, '');
                                                        if (val.length > 2) val = val.slice(0,2) + '/' + val.slice(2,4);
                                                        setCardExpiry(val);
                                                    }}
                                                    placeholder="AA/YY" 
                                                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:border-blue-500" 
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">CVC / CVV</label>
                                                <input 
                                                    type="text" 
                                                    maxLength={3}
                                                    value={cardCvc} 
                                                    onChange={e => setCardCvc(e.target.value.replace(/\D/g, ''))}
                                                    placeholder="123" 
                                                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:border-blue-500" 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 space-y-4">
                                        <h4 className="text-slate-800 font-bold text-xs uppercase tracking-wider">IBAN Transfer Bilgileri</h4>
                                        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
                                            <div className="flex justify-between text-xs">
                                                <span className="text-slate-400">Banka:</span>
                                                <span className="font-bold text-slate-900">Akbank</span>
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <span className="text-slate-400">Alıcı (Unvan):</span>
                                                <span className="font-bold text-slate-900">Supervisor Eğitim Teknolojileri A.Ş.</span>
                                            </div>
                                            <div className="flex flex-col gap-1 pt-2 border-t border-slate-100">
                                                <span className="text-[10px] text-slate-400 font-bold uppercase">IBAN Numarası:</span>
                                                <div className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2 border border-slate-200">
                                                    <span className="font-mono text-xs text-slate-800 select-all font-bold">TR92 0004 6000 1580 0724 9912 34</span>
                                                    <button 
                                                        type="button"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText("TR920004600015800724991234");
                                                            alert("IBAN kopyalandı!");
                                                        }}
                                                        className="text-[10px] font-bold text-blue-600 hover:underline"
                                                    >
                                                        Kopyala
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1 pt-2 border-t border-slate-100">
                                                <span className="text-[10px] text-slate-400 font-bold uppercase">Transfer Açıklaması:</span>
                                                <div className="bg-blue-50/50 text-blue-800 border border-blue-100 text-[11px] font-medium p-3 rounded-xl">
                                                    Lütfen havale/EFT yaparken açıklama kısmına <strong>{student.name}</strong> yazınız.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={handlePrevStep}
                                    className="flex-1 py-5 bg-slate-50 hover:bg-slate-100 text-slate-900 font-black text-[10px] uppercase tracking-widest rounded-2xl transition-all border border-slate-200"
                                >
                                    ← Geri
                                </button>
                                <button
                                    onClick={handlePurchaseRequest}
                                    disabled={submitting !== null}
                                    className="flex-[2] py-5 bg-blue-600 hover:bg-blue-700 text-white font-black text-[10px] uppercase tracking-widest rounded-2xl transition-all shadow-xl shadow-blue-500/20 disabled:opacity-50"
                                >
                                    {submitting ? 'Gönderiliyor...' : 'Ödemeyi Onayla'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer Info */}
                <div className="mt-16 text-center max-w-2xl mx-auto">
                    <div className="flex items-center justify-center gap-6 mb-8 opacity-40 grayscale hover:grayscale-0 transition-all duration-700">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-900 border border-slate-200 px-3 py-1 rounded-md">SSL Secured</span>
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-900 border border-slate-200 px-3 py-1 rounded-md">256-bit AES</span>
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-900 border border-slate-200 px-3 py-1 rounded-md">Supervisor Guard</span>
                    </div>
                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] leading-loose">
                        Paket seçtikten sonra talebiniz onay sürecine alınacaktır. Onay sonrası platforma tam erişim sağlayabilirsiniz. Adımlar arası güvenliğiniz Supervisor Eğitim Ekosistemi tarafından korunmaktadır.
                    </p>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes fade-in-up {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                }
                .animate-shake {
                    animation: shake 0.4s ease-in-out;
                }
            `}} />
        </div>
    );
};
